import React, { useEffect, useState } from "react";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import BarChartIcon from "@mui/icons-material/BarChart";
import axiosClient from "../api/axiosClient";
import { MatchCard } from "./MatchCard.tsx";
import { clearPersistedState } from "./LiveMatch/LiveMatchContext.tsx";
import { liveMatchService, configService } from "../api/service.ts";
import MatchReplayDialog from "./LiveMatch/replay/MatchReplayDialog.tsx";
import MatchStatsDialog from "./LiveMatch/replay/MatchStatsDialog.tsx";

interface MatchDialogProps {
    open: boolean;
    onClose: () => void;
    matchId: number;
    eventId?: number;
    onReset?: () => void;
}

export const MatchDialog: React.FC<MatchDialogProps> = ({ open, onClose, matchId, eventId, onReset }) => {
    const [match, setMatch] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [resetConfirm, setResetConfirm] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [hasLiveSession, setHasLiveSession] = useState(false);
    const [ourTeamName, setOurTeamName] = useState('');
    const [openReplay, setOpenReplay] = useState(false);
    const [openStats, setOpenStats]   = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        setError(null);
        setResetConfirm(false);
        setHasLiveSession(false);

        const loadMatch    = axiosClient.get(`/matches/${matchId}`).then(res => res.data);
        const loadSession  = eventId ? liveMatchService.getByEvent(eventId) : Promise.resolve(null);
        const loadTeamName = configService.getTeamName().catch(() => '');

        Promise.all([loadMatch, loadSession, loadTeamName])
            .then(([matchData, session, teamName]) => {
                setMatch(matchData);
                setHasLiveSession(session !== null);
                setOurTeamName(teamName);
            })
            .catch(err => {
                console.error(err);
                setError("Failed to load match data");
            })
            .finally(() => setLoading(false));
    }, [open, matchId, eventId]);

    async function handlePrint() {
        if (!eventId) return;
        setPrinting(true);
        try {
            const res = await axiosClient.get(`/matches/by-event/${eventId}/report`, { responseType: 'text' });
            const win = window.open('', '_blank');
            if (!win) { alert('Popup bloccato — consenti i popup per questo sito.'); return; }
            win.document.open();
            win.document.write(res.data);
            win.document.close();
        } catch {
            alert('Errore nella generazione del referto.');
        } finally {
            setPrinting(false);
        }
    }

    async function handleReset() {
        if (!eventId) return;
        setResetting(true);
        try {
            await axiosClient.delete(`/matches/by-event/${eventId}`);
        } catch {
            // ignora — idempotente
        }
        clearPersistedState(eventId);
        setResetting(false);
        onReset?.();
    }

    return (
    <>
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ backgroundColor: "primary.main" }}>
                Match Details
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {loading && <CircularProgress />}
                {error && <div>{error}</div>}
                {match && <MatchCard match={match} />}
            </DialogContent>
            {eventId && (
                <DialogActions sx={{ justifyContent: 'space-between', px: 2 }}>
                    <Stack direction="row" spacing={1}>
                    {/* Stampa referto */}
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={printing ? <CircularProgress size={14} color="inherit" /> : <PrintIcon />}
                        onClick={handlePrint}
                        disabled={printing}
                    >
                        {printing ? 'Generazione…' : 'Stampa referto'}
                    </Button>

                    {/* Visualizza match / Statistiche (solo se esiste sessione live) */}
                    {hasLiveSession && (
                        <Button
                            variant="outlined"
                            size="small"
                            color="secondary"
                            startIcon={<PlayCircleOutlineIcon />}
                            onClick={() => setOpenReplay(true)}
                        >
                            Visualizza match
                        </Button>
                    )}
                    {hasLiveSession && (
                        <Button
                            variant="outlined"
                            size="small"
                            color="info"
                            startIcon={<BarChartIcon />}
                            onClick={() => setOpenStats(true)}
                        >
                            Statistiche gara
                        </Button>
                    )}
                    </Stack>

                    {/* Reset */}
                    {onReset && (
                        !resetConfirm ? (
                            <Button variant="text" color="error" size="small" onClick={() => setResetConfirm(true)}>
                                Reset partita
                            </Button>
                        ) : (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Button
                                    variant="outlined" color="error" size="small"
                                    onClick={handleReset} disabled={resetting}
                                    startIcon={resetting ? <CircularProgress size={14} color="inherit" /> : undefined}
                                >
                                    {resetting ? 'Reset…' : 'Conferma reset'}
                                </Button>
                                <Button variant="text" size="small" onClick={() => setResetConfirm(false)}>
                                    Annulla
                                </Button>
                            </Stack>
                        )
                    )}
                </DialogActions>
            )}
        </Dialog>

        {hasLiveSession && eventId && (
            <MatchReplayDialog
                open={openReplay}
                onClose={() => setOpenReplay(false)}
                eventId={eventId}
                homeTeamName={ourTeamName || 'Noi'}
                awayTeamName={match?.opponent || 'Avversario'}
            />
        )}
        {hasLiveSession && eventId && (
            <MatchStatsDialog
                open={openStats}
                onClose={() => setOpenStats(false)}
                eventId={eventId}
                homeTeamName={ourTeamName || 'Noi'}
                awayTeamName={match?.opponent || 'Avversario'}
            />
        )}
    </>
    );
};
